import type { FC } from 'react'
import type { CardMeta } from '../dashboard/types'
import type { QHSEDefectDto } from '../../../QHSE/core/_models'
import CardInfoButton from './CardInfoButton'
import { isOpenDashboardDefect } from './dashboardData'

type DefectsOverviewProps = {
  defects: QHSEDefectDto[]
  openDefects: number
  overdueDefects: number
  onOverdue: () => void
  onViewAll: () => void
}

const meta: CardMeta = {
  description: 'Open submitted defects excluding Drafts, with observation age. No deadline is inferred.',
  endpoint: 'GET /api/qhse/defects (filter: activeOnly=true, companyGroupId)',
  logic: 'Open = active submitted defects; excludes SAVE/DRAFT/CLOSE/REVIEWED. Open >30 Days uses dateObserved; it is not a due-date count.',
}

const DefectsOverview: FC<DefectsOverviewProps> = ({ defects, openDefects, overdueDefects, onOverdue, onViewAll }) => {
  const openList = defects.filter(isOpenDashboardDefect).slice(0, 4)

  return (
    <div className='flex flex-col flex-1'>
      <div className='card flex-1' style={{ borderRadius: '16px' }}>
        <div className='card-body'>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '13px' }}>Defects Overview</h6>
            <span className='ms-auto d-flex align-items-center gap-2'>
              <CardInfoButton meta={meta} />
              <a href='#' className='fw-bold' style={{ fontSize: '10px' }} onClick={(e) => { e.preventDefault(); onViewAll(); }}>View all</a>
            </span>
          </div>
          <div className='row g-2 mb-3 text-center'>
            <div className='col-6'><div className='fw-bold text-dark fs-5'>{openDefects}</div><div className='text-muted' style={{ fontSize: '9px' }}>Open</div></div>
            <div className='col-6'><button className='btn btn-link p-0' onClick={onOverdue} aria-label={`View ${overdueDefects} overdue defects`}><span className='d-block fw-bold text-warning fs-5'>{overdueDefects}</span><span className='text-muted' style={{ fontSize: '9px' }}>Defect Overdue</span></button></div>
          </div>
          <div className='text-muted mb-2' style={{ fontSize: '10px' }}>Overdue: open more than 30 days since observation; Drafts excluded.</div>
          <div className='d-flex flex-column gap-2' style={{ fontSize: '10px' }}>
            {openList.length > 0 ? (
              openList.map((d, i) => (
                <DefectRow key={i}
                  id={d.defectNumber || `DEF-${d.id}`}
                  vessel={d.vesselName || `Vessel #${d.vesselId}`}
                  desc={(d.description || 'No description').slice(0, 40)}
                  status={d.status === 'SUBMIT' ? 'OPEN' : d.status === 'SAVE' ? 'DRAFT' : (d.status || 'OPEN')}
                  due={d.dateObserved ? new Date(d.dateObserved).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'} />
              ))
            ) : (
              <div className='text-center text-muted py-4' style={{ fontSize: '11px' }}>
                <div className='mb-1'>No open defects</div>
                <div style={{ fontSize: '10px' }}>All defects resolved or closed</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const DefectRow: FC<{ id: string; vessel: string; desc: string; status: string; due: string }> = ({ id, vessel, desc, status, due }) => (
  <div className='d-flex justify-content-between align-items-center p-2 rounded border' style={{ borderColor: '#eee', fontSize: '10px' }}>
    <div>
      <div className='d-flex align-items-center gap-1'>
        <span className='fw-semibold text-primary'>{id}</span>
        <span className='text-muted'>| {vessel}</span>
      </div>
      <div className='text-muted' style={{ fontSize: '9px' }}>{desc}</div>
    </div>
    <div className='text-end'>
      <span className={`badge ${status === 'CRITICAL' || status === 'OPEN' ? 'badge-light-danger' : 'badge-light-warning'}`} style={{ fontSize: '8px' }}>{status}</span>
      <div className='text-muted' style={{ fontSize: '9px' }}>Observed {due}</div>
    </div>
  </div>
)

export default DefectsOverview
